import type { Duration } from 'dayjs/plugin/duration';
import { useContext, useMemo } from 'react';
import tinycolor from 'tinycolor2';

import { CustomStylesCtx, useTextStyle } from '@webapp/common/components/custom-styles';
import { PointsResults } from '@webapp/common/components/point-results';
import { TimeResults } from '@webapp/common/components/time-results';
import { createDuration } from '@webapp/common/lib/date';
import { setHtml } from '@webapp/common/lib/ui';

import css from '../test-results.css';

import { CompleteText } from './complete-text';

const Header: FC<{
    background: string;
}> = ({ background }) => {
    const style = useMemo(() => {
        const c = tinycolor(background);

        c.setAlpha(Math.max(0, c.getAlpha() - 0.45));

        return {
            background: `linear-gradient(to right, ${background}, ${c.toRgbString()})`,
        };
    }, [background]);

    return <div className={css.header} style={style} />;
};

const Description: FC<{
    passed: boolean;
    testPassed: string;
    testNotPassed: string;
}> = ({ passed, testNotPassed, testPassed }) => {
    const { textStyle } = useTextStyle();

    if (!testPassed && !testNotPassed) return null;

    return <div {...setHtml(passed ? testPassed : testNotPassed)} className={css.descr} style={textStyle} />;
};

const Title: FC<{
    percent: number;
    html: any;
}> = ({ html, percent }) => (
    <div className={css.result}>
        <span {...setHtml(html + '&nbsp;')} />
        <span>{percent}%</span>
    </div>
);

const Body: FC<{
    maxScore: number;
    fact: number;
    timer: boolean;
    factTime: Duration;
    timerHours: number;
    timerMinutes: number;
    timerSeconds: number;
}> = ({ fact, factTime, maxScore, timer, timerHours, timerMinutes, timerSeconds }) => (
    <div className={css.body}>
        {typeof maxScore === 'number' && isFinite(maxScore) && (
            <PointsResults fact={fact} max={maxScore} pointsLabel='' />
        )}
        {timer && (
            <TimeResults factTime={factTime} targetTime={createDuration(timerHours, timerMinutes, timerSeconds)} />
        )}
    </div>
);

export const ResultsWidget: FC<{
    buttonStyles: { root?: CSSProperties };
    params: any;
    percent: number;
    passed: boolean;
    maxScore: number;
    timeAgo: Duration;
    fact: number;
    titleStyle: CSSProperties;
    completeText?: string;
}> = ({
    buttonStyles: {
        root: { backgroundColor },
    },
    fact,
    maxScore,
    params: {
        main: { timer, timerHours, timerMinutes, timerSeconds },
        tests: { testNotPassed, testPassed, testResultLabel },
    },
    passed,
    percent,
    timeAgo,
    completeText,
}) => {
    const {
        question: { text: questionTextStyles },
    } = useContext(CustomStylesCtx);
    return (
        <div className={css.testResults}>
            <Header background={backgroundColor} />
            <div className={css.content}>
                {isFinite(percent) && <Title html={testResultLabel} percent={percent} />}
                {passed !== null && (
                    <Description passed={passed} testNotPassed={testNotPassed} testPassed={testPassed} />
                )}
                <Body
                    fact={fact}
                    factTime={timeAgo}
                    maxScore={maxScore}
                    timer={timer}
                    timerHours={timerHours}
                    timerMinutes={timerMinutes}
                    timerSeconds={timerSeconds}
                />
                {completeText ? <CompleteText style={questionTextStyles} completePageText={completeText} /> : null}
            </div>
        </div>
    );
};
